import { create } from 'zustand';

export interface CheckoutStep {
  stepId: string;
  uiRef: string;
  containerIdentifier?: string;
  fields?: any[];
  level?: string;
  offerContainerId?: string;
  sequence?: string;
  visible?: boolean;
}

export interface CheckoutSection {
  sectionId: string;
  name?: string;
  steps?: CheckoutStep[];
  [key: string]: any;
}

const STATIC_SECTIONS: CheckoutSection[] = [
  { sectionId: "OTPVerification" },
  { sectionId: "OrderSummary" },
  { sectionId: "SuccessPage" },
];

export interface CheckoutStructure {
  sections: CheckoutSection[];
  [key: string]: any;
}

export interface CheckoutStore {
  structure: CheckoutStructure | null;
  currentSection: CheckoutSection | null;
  sectionsToVisit: CheckoutSection[];
  sectionsVisited: CheckoutSection[];
  currentStep: CheckoutStep | null;
  stepsToVisit: CheckoutStep[];
  stepsVisited: CheckoutStep[];
  capturedData: any[];
  setStructure: (structure: CheckoutStructure) => void;
  updateStructure: (structure: CheckoutStructure) => void;
  goToNextSection: () => void;
  goToPreviousSection: () => void;
  goToNextStep: () => void;
  goToPreviousStep: () => void;
  goToStep: (uiRef: string) => void;
  goToSection: (sectionId: string) => void;
  setCapturedData: (section: any) => void;
  resetCheckout: () => void;
}

const initialState = {
  structure: null,
  currentSection: null,
  sectionsToVisit: [],
  sectionsVisited: [],
  currentStep: null,
  stepsToVisit: [],
  stepsVisited: [],
  capturedData: [],
};

export const useCheckoutStore = create<CheckoutStore>((set, get) => ({
  ...initialState,

  setStructure: (structure: CheckoutStructure) => {
    const { sectionsVisited } = get();
    const sectionsToVisit = [...(structure.sections ?? []), ...STATIC_SECTIONS];
    const currentSection = sectionsToVisit[sectionsVisited.length] ?? null;
    const stepsToVisit = currentSection?.steps ?? [];
    const currentStep = stepsToVisit[0] ?? null;

    set({ structure, sectionsToVisit, currentSection, stepsToVisit, stepsVisited: [], currentStep });
  },

  updateStructure: (structure: CheckoutStructure) => {
    const { currentSection, stepsVisited } = get();
    const allSections = [...(structure.sections ?? []), ...STATIC_SECTIONS];
    const updatedCurrentSection = allSections.find(
      (s) => s.sectionId === currentSection?.sectionId
    ) ?? currentSection;
    const stepsToVisit = updatedCurrentSection?.steps ?? [];
    const currentStep = stepsToVisit[stepsVisited.length] ?? null;

    set({ structure, currentSection: updatedCurrentSection, stepsToVisit, currentStep });
  },

  goToNextSection: () => {
    const { sectionsToVisit, sectionsVisited, currentSection } = get();
    if (!currentSection) return;

    const updatedVisited = [...sectionsVisited, currentSection];
    const nextSection = sectionsToVisit[updatedVisited.length] ?? null;
    const stepsToVisit = nextSection?.steps ?? [];
    const currentStep = stepsToVisit[0] ?? null;

    set({ sectionsVisited: updatedVisited, currentSection: nextSection, stepsToVisit, stepsVisited: [], currentStep });
  },

  goToPreviousSection: () => {
    const { sectionsVisited, sectionsToVisit } = get();
    if (sectionsVisited.length === 0) return;

    const updatedVisited = sectionsVisited.slice(0, -1);
    const previousSection = sectionsToVisit[updatedVisited.length] ?? null;
    const stepsToVisit = previousSection?.steps ?? [];
    const currentStep = stepsToVisit[stepsToVisit.length - 1] ?? null;
    const stepsVisited = stepsToVisit.slice(0, -1);

    set({ sectionsVisited: updatedVisited, currentSection: previousSection, stepsToVisit, stepsVisited, currentStep });
  },

  goToNextStep: () => {
    const { stepsToVisit, stepsVisited, currentStep } = get();
    if (!currentStep) return;

    const updatedStepsVisited = [...stepsVisited, currentStep];
    const nextStep = stepsToVisit[updatedStepsVisited.length] ?? null;

    if (nextStep) {
      set({ stepsVisited: updatedStepsVisited, currentStep: nextStep });
    } else {
      get().goToNextSection();
    }
  },

  goToPreviousStep: () => {
    const { stepsVisited, stepsToVisit } = get();

    if (stepsVisited.length === 0) {
      get().goToPreviousSection();
      return;
    }

    const updatedStepsVisited = stepsVisited.slice(0, -1);
    const previousStep = stepsToVisit[updatedStepsVisited.length] ?? null;

    set({ stepsVisited: updatedStepsVisited, currentStep: previousStep });
  },
  goToStep: (uiRef: string) => {
    const { sectionsToVisit } = get();

    for (let sIdx = 0; sIdx < sectionsToVisit.length; sIdx++) {
      const section = sectionsToVisit[sIdx];
      const steps = section?.steps ?? [];
      const stepIdx = steps.findIndex((s) => s.uiRef === uiRef);

      if (stepIdx !== -1) {
        set({
          currentSection: section,
          sectionsVisited: sectionsToVisit.slice(0, sIdx),
          stepsToVisit: steps,
          currentStep: steps[stepIdx],
          stepsVisited: steps.slice(0, stepIdx),
        });
        return;
      }
    }

    console.warn(`goToStep: no step found with uiRef "${uiRef}"`);
  },
  goToSection: (sectionId: string) => {
    const { sectionsToVisit } = get();
    const sIdx = sectionsToVisit.findIndex((s) => s.sectionId === sectionId);

    if (sIdx === -1) {
      console.warn(`goToSection: no section found with sectionId "${sectionId}"`);
      return;
    }

    const section = sectionsToVisit[sIdx];
    const stepsToVisit = section?.steps ?? [];

    set({
      currentSection: section,
      sectionsVisited: sectionsToVisit.slice(0, sIdx),
      stepsToVisit,
      currentStep: stepsToVisit[0] ?? null,
      stepsVisited: [],
    });
  },

  setCapturedData: (section: any) => {
    set((state) => ({
      capturedData: [
        ...state.capturedData.filter((s) => s.sectionId !== section.sectionId),
        section,
      ],
    }));
  },

  resetCheckout: () => set(initialState),
}));